// 🎨 관리자 신청서 상세 페이지 - 고급스러운 풀스크린 디자인
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Print as PrintIcon,
} from '@mui/icons-material';

// 🔧 Hooks and Utils
import { useApplications } from './hooks/useApplications';
import { useOrderDetailForm } from './components/orderDetail/hooks/useOrderDetailForm';
import { useAlertManagement } from './components/orderDetail/hooks/useAlertManagement';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import type { Application } from './types/application';

// 🎨 Components (곧 생성할 예정)
import ApplicationStickyHeader from './components/applicationDetail/ApplicationStickyHeader';
import DeviceInspectionTab from './components/applicationDetail/DeviceInspectionTab';
import ApplicationInfoTab from './components/applicationDetail/ApplicationInfoTab';
import ProcessHistoryTab from './components/applicationDetail/ProcessHistoryTab';
import AlertTab from './components/orderDetail/components/AlertTab';
import MemoTab from './components/orderDetail/components/MemoTab';

// 🎯 탭 정의
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const AdminApplicationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // 🎯 상태 관리
  const [activeTab, setActiveTab] = useState(0);
  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 🔍 데이터 로딩
  const { applications } = useApplications();

  // 🎯 기능 훅들 (application이 로드된 후 사용)
  const alertHook = useAlertManagement(application);
  const formHook = useOrderDetailForm(
    application, 
    'admin@dwt.kr', // 관리자 ID
    (updatedApp) => {
      setApplication(prev => prev ? { ...prev, ...updatedApp } : null);
    }
  );

  useEffect(() => {
    if (!id) {
      setError('신청서 ID가 없습니다.');
      setLoading(false);
      return;
    }

    if (applications.length > 0) {
      const foundApp = applications.find(app => app.id === id);
      if (foundApp) {
        setApplication(foundApp);
        setError(null);
      } else {
        setError('해당 신청서를 찾을 수 없습니다.');
      }
      setLoading(false);
    }
  }, [id, applications]);

  // 🎨 탭 변경 핸들러
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // 🔄 새로고침 핸들러
  const handleRefresh = () => {
    setLoading(true);
    // 실제 새로고침 로직은 useApplications 훅에서 처리
    setTimeout(() => setLoading(false), 1000);
  };

  // ⚙️ 상태 변경 핸들러
  const handleStatusChange = async (newStatus: string) => {
    if (!application || !formHook) return;

    try {
      console.log(`🔄 상태 변경 시작: ${application.status} → ${newStatus}`);
      
      // 🎯 직접 새로운 상태로 저장 (React 비동기 상태 업데이트 문제 해결)
      await formHook.handleSave({ status: newStatus });
      
      // 🎯 로컬 상태도 업데이트 (UI 즉시 반영)
      if (formHook.setFormData) {
        formHook.setFormData(prev => ({ ...prev, status: newStatus }));
      }
      
      setApplication(prev => prev ? { 
        ...prev, 
        status: newStatus as Application['status']
        // 🔄 기기 상태 변경은 useOrderDetailForm.ts의 handleSave에서 처리함
      } : prev);
      
      console.log(`✅ 상태 변경 완료: ${newStatus}`);
    } catch (error) {
      console.error('상태 변경 실패:', error);
      throw error; // ApplicationStickyHeader에서 에러 처리하도록
    }
  };

  // 🗑️ 신청서 삭제 핸들러
  const handleDelete = async () => {
    if (!application?.id) return;

    try {
      // Firestore에서 신청서 삭제
      const applicationRef = doc(db, 'applications', application.id);
      await deleteDoc(applicationRef);
      
      // 성공적으로 삭제 후 목록 페이지로 이동
      navigate('/admin/orders');
    } catch (error) {
      console.error('신청서 삭제 실패:', error);
      throw error; // ApplicationStickyHeader에서 에러 처리하도록
    }
  };

  // 🚚 택배송장 업데이트 핸들러 (화면 재갱신 없이 즉시 반영)
  const handleTrackingUpdate = (trackingData: { type: 'delivery' | 'return', number: string }) => {
    if (!application) return;

    console.log(`🚚 택배송장 즉시 업데이트: ${trackingData.type} → ${trackingData.number}`);

    setApplication(prev => {
      if (!prev) return null;
      
      if (trackingData.type === 'delivery') {
        return { ...prev, tracking_number: trackingData.number };
      } else {
        return { ...prev, return_tracking_number: trackingData.number };
      }
    });
  };



  // 🎨 로딩 스켈레톤
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3, mb: 3 }} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </Container>
    );
  }

  // ❌ 에러 상태
  if (error || !application) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2, mb: 3 }}>
          {error || '신청서를 불러올 수 없습니다.'}
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/admin/orders')}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
      </Container>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fafd',
        pb: 4
      }}>
        {/* 🔒 Sticky Header - 고급스러운 디자인 */}
        <Box
          sx={{
            position: 'sticky',
            top: 64, // AdminLayout AppBar 높이 고려
            zIndex: theme.zIndex.appBar - 1,
            mb: 3,
          }}
        >
                  <ApplicationStickyHeader
          application={application}
          onBack={() => navigate('/admin/orders')}
          onRefresh={handleRefresh}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
          onTrackingUpdate={handleTrackingUpdate}
          loading={loading}
        />
        </Box>

        <Container maxWidth="xl">
          {/* 📑 탭 네비게이션 - 세련된 디자인 */}
          <Zoom in timeout={600}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e1e8ed',
                bgcolor: '#fff',
                overflow: 'hidden',
                mb: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={isMobile ? 'scrollable' : 'fullWidth'}
                scrollButtons="auto"
                sx={{
                  borderBottom: '1px solid #e1e8ed',
                  minHeight: 64,
                  '& .MuiTab-root': {
                    minHeight: 64,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#64748b',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      color: '#1976d2',
                      bgcolor: 'rgba(25, 118, 210, 0.04)',
                    },
                    '&.Mui-selected': {
                      color: '#1976d2',
                      fontWeight: 700,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  },
                  '& .MuiTabs-flexContainer': {
                    height: 64,
                  },
                }}
              >
                <Tab label="🔍 기기 검수" />
                <Tab label="📋 신청 정보" />
                <Tab label="📊 처리 이력" />
                <Tab label="📨 알림톡" />
                <Tab label="📝 메모" />
              </Tabs>

              {/* 🎯 탭 컨텐츠 */}
              <Box sx={{ minHeight: 500 }}>
                <TabPanel value={activeTab} index={0}>
                  <DeviceInspectionTab 
                    application={application}
                    onUpdate={setApplication}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                  <ApplicationInfoTab 
                    application={application} 
                    onUpdate={setApplication}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                  <ProcessHistoryTab application={application} />
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                  <AlertTab
                    application={application}
                    alertState={alertHook.alertState}
                    alertPreviewMessage={alertHook.alertPreviewMessage}
                    isAlertFormValid={alertHook.isAlertFormValid}
                    onTemplateSelect={alertHook.handleTemplateSelect}
                    onRepairReasonChange={alertHook.handleRepairReasonChange}
                    onReturnTrackingChange={alertHook.handleReturnTrackingChange}
                    onSendClick={alertHook.handleSendClick}
                    onConfirmSend={alertHook.handleConfirmSend}
                    onSetConfirmDialog={alertHook.setConfirmDialog}
                    onSetSnackbar={alertHook.setSnackbar}
                  />
                </TabPanel>

                <TabPanel value={activeTab} index={4}>
                  <MemoTab
                    value={formHook.formData.remark}
                    onChange={formHook.handleRemarkChange}
                    onSave={formHook.handleSave}
                    hasChanges={formHook.hasChanges}
                    loading={formHook.loading}
                    lastSaved={application?.updated_at ? new Date(application.updated_at.seconds * 1000) : null}
                  />
                </TabPanel>
              </Box>
            </Paper>
          </Zoom>
        </Container>
      </Box>
    </Fade>
  );
};

export default AdminApplicationDetailPage;